import './issuers.scss'

import { Button, Drawer, Skeleton, Table, Typography } from 'antd'
import classnames from 'classnames'
import { useEffect, useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchByDomain } from '../../../../api'
import { buildTableFilterKeys } from '../../../../components/tables/build-table-filter-keys'
import { formatRating } from '../../../../formatters/format-rating'
import { getDefaultDomainByEnvironment } from '../../../../models'
import { getEnvironment } from '../../../../util/environment'
import { drawerAndModalWidth } from '../../../../util/guideline'
import { sortStrings } from '../../../../util/sorters/sort-strings'
import { Issuer, IssuerTier, transformIssuersToTableItems } from '../../models'
import { CreateIssuer } from '../CreateIssuer'
import { UpdateIssuer } from '../UpdateIssuer'
import { UpdateIssuerTierDrawer } from '../UpdateIssuerTier'
import { buildIssuersTableColumns } from './issuers-table-columns'

export const Issuers = () => {
  const environment = getEnvironment()
  const [issuerToUpdate, setIssuerToUpdate] = useState(null)
  const [issuerToUpdateTier, setIssuerToUpdateTier] = useState(null)

  const [isCreateIssuerDrawerOpen, setIsCreteIssuerDrawerOpen] = useState(false)
  const [filters, setFilters] = useState({ longRatings: [], shortRatings: [] })

  const { data: issuers, mutate } = useSWR<Issuer[]>(
    ['/issuers', getDefaultDomainByEnvironment(environment)],
    fetchByDomain,
  )

  const { data: issuerTiers, mutate: mutateTiers } = useSWR<IssuerTier[]>(
    ['/issuers/tiers', getDefaultDomainByEnvironment(environment)],
    fetchByDomain,
  )

  useEffect(() => {
    issuers &&
      setFilters({
        longRatings: buildTableFilterKeys(issuers, 'longRating', formatRating),
        shortRatings: buildTableFilterKeys(issuers, 'shortRating', formatRating),
      })
  }, [issuers])

  const columns = buildIssuersTableColumns({
    filters,
    onUpdate: setIssuerToUpdate,
    onUpdateIssuerTier: setIssuerToUpdateTier,
  })

  const dataSource = transformIssuersToTableItems(issuers, issuerTiers)

  const onCloseDialogs = () => {
    setIssuerToUpdate(null)
    setIssuerToUpdateTier(null)
    setIsCreteIssuerDrawerOpen(false)
  }

  const onCreateOrUpdateIssuer = async () => {
    await mutateTiers()
    await mutate()
    onCloseDialogs()
  }

  return (
    <Column className="issuers">
      <Typography.Title>Issuers</Typography.Title>

      {!issuers ? (
        <Skeleton />
      ) : (
        <Column className="issuers">
          <Row justifyContentEnd>
            <Button type="primary" onClick={() => setIsCreteIssuerDrawerOpen(true)}>
              Create Issuer
            </Button>
          </Row>

          <Table
            rowClassName={(issuer: Issuer) =>
              classnames({ active: issuer.id === (issuerToUpdate?.id || issuerToUpdateTier?.id) })
            }
            dataSource={dataSource.sort(byName)}
            loading={!issuers}
            columns={columns}
            pagination={false}
            rowKey="id"
            size="small"
          />

          <Drawer
            title={`${isCreateIssuerDrawerOpen ? 'Create' : 'Update'} Issuer`}
            placement="right"
            onClose={onCloseDialogs}
            open={isCreateIssuerDrawerOpen || issuerToUpdate}
            width={drawerAndModalWidth}
            destroyOnClose
          >
            {isCreateIssuerDrawerOpen ? (
              <CreateIssuer onSuccess={onCreateOrUpdateIssuer} />
            ) : (
              <UpdateIssuer issuer={issuerToUpdate} onSuccess={onCreateOrUpdateIssuer} />
            )}
          </Drawer>

          <UpdateIssuerTierDrawer
            isVisible={issuerToUpdateTier}
            issuerId={issuerToUpdateTier?.id}
            tiers={issuerToUpdateTier?.tiers}
            onClose={onCloseDialogs}
            onSuccess={onCreateOrUpdateIssuer}
          />
        </Column>
      )}
    </Column>
  )
}

const byName = (firstIssuer: Issuer, secondIssuer: Issuer) => sortStrings(firstIssuer.name, secondIssuer.name)
